/**
 * DriverScheduleEditor
 * Veckoschema och användningsläge för kartvisning och tjänst/privat-klassificering
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '@/store/languageStore';
import {
  driverService,
  ScheduleEntry,
  ScheduleRow,
  DriverScheduleMode,
} from '@/services/drivers/driverService';
import type { DriverScheduleDraft } from '@/types/driverSetupDraft';
import { organizationsService } from '@/services/organizations/organizationsService';
import { buildWeekdayServiceScheduleFromOrg } from '@/utils/buildDriverScheduleFromOrgDefaults';
import { ClockIcon, CheckIcon } from '@heroicons/react/24/outline';

interface DriverScheduleEditorProps {
  driverId?: number;
  readOnly?: boolean;
  organizationId?: string;
  draftMode?: boolean;
  /** Återställer valt schema när fliken monteras om vid skapande. */
  initialDraft?: DriverScheduleDraft | null;
  onDraftChange?: (draft: DriverScheduleDraft) => void;
}

const DAY_KEYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

const WEEKDAY_INDICES = [0, 1, 2, 3, 4];
const WEEKEND_INDICES = [5, 6];

const DEFAULT_START = '08:00';
const DEFAULT_END = '17:00';

const SCHEDULE_MODES: DriverScheduleMode[] = ['private_manual', 'work_hours', 'company_car'];

interface DaySchedule {
  enabled: boolean;
  start_time: string;
  end_time: string;
}

function emptyWeekSchedule(): DaySchedule[] {
  return Array(7)
    .fill(null)
    .map((_, i) => ({
      enabled: WEEKDAY_INDICES.includes(i),
      start_time: DEFAULT_START,
      end_time: DEFAULT_END,
    }));
}

function scheduleFromRows(rows: ScheduleRow[]): DaySchedule[] {
  const newSchedule: DaySchedule[] = Array(7)
    .fill(null)
    .map(() => ({
      enabled: false,
      start_time: DEFAULT_START,
      end_time: DEFAULT_END,
    }));

  rows.forEach((row) => {
    if (row.day_of_week >= 0 && row.day_of_week <= 6) {
      newSchedule[row.day_of_week] = {
        enabled: true,
        start_time: row.start_time,
        end_time: row.end_time,
      };
    }
  });

  return newSchedule;
}

function cloneSchedule(days: DaySchedule[]): DaySchedule[] {
  return days.map((d) => ({ ...d }));
}

function buildDraftFromState(
  mode: DriverScheduleMode,
  schedule: DaySchedule[],
  sameForAll: boolean,
  commonStart: string,
  commonEnd: string
): DriverScheduleDraft {
  const payload: DriverScheduleDraft = { mode };
  if (mode === 'work_hours') {
    payload.schedule = schedule
      .map((day, index) => ({
        day_of_week: index,
        start_time: sameForAll ? commonStart : day.start_time,
        end_time: sameForAll ? commonEnd : day.end_time,
        enabled: day.enabled,
      }))
      .filter((d) => d.enabled)
      .map(({ enabled: _e, ...rest }) => rest);
  }
  return payload;
}

const DriverScheduleEditor: React.FC<DriverScheduleEditorProps> = ({
  driverId,
  readOnly = false,
  organizationId,
  draftMode = false,
  initialDraft = null,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const draftInitializedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingOrg, setApplyingOrg] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [scheduleMode, setScheduleMode] = useState<DriverScheduleMode>('private_manual');
  const [sameForAll, setSameForAll] = useState(true);
  const [commonStart, setCommonStart] = useState(DEFAULT_START);
  const [commonEnd, setCommonEnd] = useState(DEFAULT_END);
  const [schedule, setSchedule] = useState<DaySchedule[]>(emptyWeekSchedule);
  const workHoursSnapshotRef = useRef<DaySchedule[]>(emptyWeekSchedule());

  const applyModeToDisplay = useCallback((mode: DriverScheduleMode, base: DaySchedule[]) => {
    if (mode === 'private_manual') {
      setSchedule(
        base.map((day) => ({
          ...day,
          enabled: false,
        }))
      );
      return;
    }
    if (mode === 'company_car') {
      setSchedule(
        base.map((day) => ({
          ...day,
          enabled: true,
        }))
      );
      return;
    }
    setSchedule(cloneSchedule(base));
    const enabledDays = base.filter((d) => d.enabled);
    if (enabledDays.length > 0) {
      const allSame = enabledDays.every(
        (d) => d.start_time === enabledDays[0].start_time && d.end_time === enabledDays[0].end_time
      );
      setSameForAll(allSame);
      setCommonStart(enabledDays[0].start_time);
      setCommonEnd(enabledDays[0].end_time);
    }
  }, []);

  const emitDraft = useCallback(
    (
      mode: DriverScheduleMode,
      days: DaySchedule[],
      sameAll: boolean,
      cStart: string,
      cEnd: string
    ) => {
      if (draftMode && onDraftChangeRef.current) {
        onDraftChangeRef.current(buildDraftFromState(mode, days, sameAll, cStart, cEnd));
      }
    },
    [draftMode]
  );

  const applyInitialDraft = useCallback(
    (draft: DriverScheduleDraft) => {
      const fromDraft =
        draft.mode === 'work_hours' && draft.schedule?.length
          ? scheduleFromRows(
              draft.schedule.map((e, i) => ({
                ...e,
                id: i,
                driver_id: 0,
                created_at: '',
                updated_at: '',
              }))
            )
          : emptyWeekSchedule();
      workHoursSnapshotRef.current = cloneSchedule(fromDraft);
      setScheduleMode(draft.mode);
      applyModeToDisplay(draft.mode, fromDraft);
    },
    [applyModeToDisplay]
  );

  const loadSchedule = useCallback(async () => {
    if (draftMode) {
      if (draftInitializedRef.current) return;
      draftInitializedRef.current = true;
      setLoading(false);
      if (initialDraft) {
        applyInitialDraft(initialDraft);
        return;
      }
      if (organizationId) {
        try {
          const orgData = await organizationsService.getDrivingLogDefaults(organizationId);
          const entries = buildWeekdayServiceScheduleFromOrg(orgData.driving_log_settings);
          const prefilled = scheduleFromRows(
            entries.map((e, i) => ({
              ...e,
              id: i,
              driver_id: 0,
              created_at: '',
              updated_at: '',
            }))
          );
          workHoursSnapshotRef.current = cloneSchedule(prefilled);
          setScheduleMode('work_hours');
          applyModeToDisplay('work_hours', prefilled);
          emitDraft('work_hours', prefilled, true, entries[0]?.start_time ?? DEFAULT_START, entries[0]?.end_time ?? DEFAULT_END);
        } catch {
          const empty = emptyWeekSchedule();
          workHoursSnapshotRef.current = cloneSchedule(empty);
          applyModeToDisplay('private_manual', empty);
          emitDraft('private_manual', empty, true, DEFAULT_START, DEFAULT_END);
        }
      }
      return;
    }
    if (!driverId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = await driverService.getDriverSchedule(driverId);
      setScheduleMode(payload.mode);

      if (payload.schedule.length === 0 && payload.mode === 'work_hours' && organizationId) {
        try {
          const orgData = await organizationsService.getDrivingLogDefaults(organizationId);
          const entries = buildWeekdayServiceScheduleFromOrg(orgData.driving_log_settings);
          const prefilled = scheduleFromRows(
            entries.map((e, i) => ({
              ...e,
              id: i,
              driver_id: driverId,
              created_at: '',
              updated_at: '',
            }))
          );
          workHoursSnapshotRef.current = cloneSchedule(prefilled);
          applyModeToDisplay(payload.mode, prefilled);
          setSuccess(t('drivers.schedule.emptyPrefilledFromOrg'));
          setTimeout(() => setSuccess(null), 6000);
          return;
        } catch {
          /* fall through */
        }
      }

      const fromApi = payload.schedule.length > 0 ? scheduleFromRows(payload.schedule) : emptyWeekSchedule();
      workHoursSnapshotRef.current = cloneSchedule(fromApi);
      applyModeToDisplay(payload.mode, fromApi);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [driverId, organizationId, t, applyModeToDisplay, draftMode, emitDraft, initialDraft, applyInitialDraft]);

  useEffect(() => {
    draftInitializedRef.current = false;
  }, [organizationId, draftMode]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleModeChange = (mode: DriverScheduleMode) => {
    if (readOnly) return;
    setScheduleMode(mode);
    applyModeToDisplay(mode, workHoursSnapshotRef.current);
    const display =
      mode === 'private_manual'
        ? workHoursSnapshotRef.current.map((d) => ({ ...d, enabled: false }))
        : mode === 'company_car'
          ? workHoursSnapshotRef.current.map((d) => ({ ...d, enabled: true }))
          : cloneSchedule(workHoursSnapshotRef.current);
    emitDraft(mode, display, sameForAll, commonStart, commonEnd);
  };

  const handleApplyOrganizationDefaults = async () => {
    if (!organizationId) {
      setError(t('drivers.schedule.applyOrgDefaultsNoOrg'));
      return;
    }
    setApplyingOrg(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await organizationsService.getDrivingLogDefaults(organizationId);
      const entries = buildWeekdayServiceScheduleFromOrg(data.driving_log_settings);
      if (entries.length === 0) {
        setError(t('drivers.schedule.applyOrgDefaultsMissing'));
        return;
      }
      const prefilled = scheduleFromRows(
        entries.map((e, i) => ({
          ...e,
          id: i,
          driver_id: driverId ?? 0,
          created_at: '',
          updated_at: '',
        }))
      );
      workHoursSnapshotRef.current = cloneSchedule(prefilled);
      setScheduleMode('work_hours');
      applyModeToDisplay('work_hours', prefilled);
      emitDraft('work_hours', prefilled, true, entries[0].start_time, entries[0].end_time);
      if (!draftMode) {
        setSuccess(t('drivers.schedule.orgDefaultsReady'));
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('common.error'));
    } finally {
      setApplyingOrg(false);
    }
  };

  const handleSave = async () => {
    if (draftMode) {
      emitDraft(scheduleMode, schedule, sameForAll, commonStart, commonEnd);
      setSuccess(t('drivers.schedule.saveDraftSuccess'));
      setTimeout(() => setSuccess(null), 4000);
      return;
    }
    if (!driverId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: { mode: DriverScheduleMode; schedule?: ScheduleEntry[] } = {
        mode: scheduleMode,
      };

      if (scheduleMode === 'work_hours') {
        payload.schedule = schedule
          .map((day, index) => ({
            day_of_week: index,
            start_time: sameForAll ? commonStart : day.start_time,
            end_time: sameForAll ? commonEnd : day.end_time,
            enabled: day.enabled,
          }))
          .filter((d) => d.enabled)
          .map(({ enabled: _e, ...rest }) => rest);
        workHoursSnapshotRef.current = cloneSchedule(schedule);
      }

      const result = await driverService.setDriverSchedule(driverId, payload);
      setScheduleMode(result.mode);
      workHoursSnapshotRef.current = scheduleFromRows(result.schedule);
      applyModeToDisplay(result.mode, workHoursSnapshotRef.current);
      setSuccess(t('drivers.schedule.saveSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (index: number) => {
    if (readOnly || scheduleMode !== 'work_hours') return;
    const newSchedule = [...schedule];
    newSchedule[index] = { ...newSchedule[index], enabled: !newSchedule[index].enabled };
    if (newSchedule[index].enabled && sameForAll) {
      newSchedule[index].start_time = commonStart;
      newSchedule[index].end_time = commonEnd;
    }
    setSchedule(newSchedule);
    workHoursSnapshotRef.current = cloneSchedule(newSchedule);
    emitDraft(scheduleMode, newSchedule, sameForAll, commonStart, commonEnd);
  };

  const updateTime = (index: number, field: 'start_time' | 'end_time', value: string) => {
    if (readOnly || scheduleMode !== 'work_hours') return;
    const newSchedule = [...schedule];
    newSchedule[index] = { ...newSchedule[index], [field]: value };
    setSchedule(newSchedule);
    workHoursSnapshotRef.current = cloneSchedule(newSchedule);
    emitDraft(scheduleMode, newSchedule, sameForAll, commonStart, commonEnd);
  };

  const handleCommonTimeChange = (field: 'start' | 'end', value: string) => {
    if (scheduleMode !== 'work_hours') return;
    if (field === 'start') {
      setCommonStart(value);
    } else {
      setCommonEnd(value);
    }
    const newSchedule = schedule.map((day) =>
      day.enabled ? { ...day, [field === 'start' ? 'start_time' : 'end_time']: value } : day
    );
    setSchedule(newSchedule);
    workHoursSnapshotRef.current = cloneSchedule(newSchedule);
    const cStart = field === 'start' ? value : commonStart;
    const cEnd = field === 'end' ? value : commonEnd;
    emitDraft(scheduleMode, newSchedule, sameForAll, cStart, cEnd);
  };

  const handleSameForAllChange = (checked: boolean) => {
    if (scheduleMode !== 'work_hours') return;
    setSameForAll(checked);
    if (checked) {
      const anyEnabled = schedule.some((d) => d.enabled);
      const firstEnabled = schedule.find((d) => d.enabled);
      const timeToUse = firstEnabled?.start_time || DEFAULT_START;
      const endToUse = firstEnabled?.end_time || DEFAULT_END;

      setCommonStart(timeToUse);
      setCommonEnd(endToUse);

      if (!anyEnabled) {
        const newSchedule = schedule.map((day, i) => ({
          ...day,
          enabled: WEEKDAY_INDICES.includes(i),
          start_time: timeToUse,
          end_time: endToUse,
        }));
        setSchedule(newSchedule);
        workHoursSnapshotRef.current = cloneSchedule(newSchedule);
        emitDraft(scheduleMode, newSchedule, true, timeToUse, endToUse);
      } else {
        const newSchedule = schedule.map((day) =>
          day.enabled ? { ...day, start_time: timeToUse, end_time: endToUse } : day
        );
        setSchedule(newSchedule);
        workHoursSnapshotRef.current = cloneSchedule(newSchedule);
        emitDraft(scheduleMode, newSchedule, true, timeToUse, endToUse);
      }
    }
  };

  const isWorkHoursMode = scheduleMode === 'work_hours';
  const enabledCount = schedule.filter((d) => d.enabled).length;
  const weekdayCount = WEEKDAY_INDICES.filter((i) => schedule[i].enabled).length;
  const weekendCount = WEEKEND_INDICES.filter((i) => schedule[i].enabled).length;

  const modeHelpKey: Record<DriverScheduleMode, string> = {
    private_manual: 'drivers.schedule.mode.privateManualHelp',
    work_hours: 'drivers.schedule.mode.workHoursHelp',
    company_car: 'drivers.schedule.mode.companyCarHelp',
  };

  if (loading) {
    return (
      <div className="text-sm text-textTertiary p-4">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <ClockIcon className="w-5 h-5 text-textSecondary" />
        <h4 className="text-sm font-semibold text-textDefault">{t('drivers.schedule.title')}</h4>
      </div>

      <p className="text-xs text-textTertiary">
        {t('drivers.schedule.description')}
      </p>

      {draftMode && (
        <p className="text-xs text-primary/90 bg-BgPrimaryLight/20 border border-primary/20 rounded-lg px-3 py-2">
          {t('userFormFields.driverDraftHint')}
        </p>
      )}

      {!readOnly && (
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-textSecondary mb-1">
            {t('drivers.schedule.modeLabel')}
          </legend>
          {SCHEDULE_MODES.map((mode) => (
            <label
              key={mode}
              className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                scheduleMode === mode
                  ? 'border-primary bg-BgPrimaryLight/20'
                  : 'border-border bg-bgLight/30 hover:bg-bgLight/50'
              }`}
            >
              <input
                type="radio"
                name={`schedule-mode-${driverId}`}
                checked={scheduleMode === mode}
                onChange={() => handleModeChange(mode)}
                className="mt-0.5 text-primary focus:ring-primary"
              />
              <span className="text-sm text-textDefault">
                {t(
                  mode === 'private_manual'
                    ? 'drivers.schedule.mode.privateManual'
                    : mode === 'work_hours'
                      ? 'drivers.schedule.mode.workHours'
                      : 'drivers.schedule.mode.companyCar'
                )}
              </span>
            </label>
          ))}
          <p className="text-xs text-textTertiary">{t(modeHelpKey[scheduleMode])}</p>
        </fieldset>
      )}

      {readOnly && (
        <p className="text-sm text-textSecondary">
          {t(
            scheduleMode === 'private_manual'
              ? 'drivers.schedule.mode.privateManual'
              : scheduleMode === 'work_hours'
                ? 'drivers.schedule.mode.workHours'
                : 'drivers.schedule.mode.companyCar'
          )}
        </p>
      )}

      {!readOnly && isWorkHoursMode && organizationId && (
        <div className="p-3 rounded-lg border border-border bg-bgLight/30 space-y-1">
          <button
            type="button"
            onClick={handleApplyOrganizationDefaults}
            disabled={applyingOrg}
            className="text-sm px-3 py-1.5 rounded-lg bg-bgElevated border border-border text-textDefault hover:bg-bgLight disabled:opacity-50"
          >
            {applyingOrg ? t('common.loading') : t('drivers.schedule.applyOrgDefaults')}
          </button>
          <p className="text-xs text-textTertiary">
            {t(draftMode ? 'userFormFields.driverDraftHint' : 'drivers.schedule.applyOrgDefaultsHelp')}
          </p>
        </div>
      )}

      {success && (
        <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-green-700 dark:text-green-300 text-xs">
          {success}
        </div>
      )}
      {error && (
        <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-xs">
          {error}
        </div>
      )}

      {!readOnly && isWorkHoursMode && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={sameForAll}
            onChange={(e) => handleSameForAllChange(e.target.checked)}
            className="rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-textSecondary">{t('drivers.schedule.sameAllDays')}</span>
        </label>
      )}

      {isWorkHoursMode && sameForAll && !readOnly && (
        <div className="p-3 bg-BgPrimaryLight/20 border border-primary/20 rounded-lg">
          <p className="text-xs font-medium text-textSecondary mb-2">
            {t('drivers.schedule.commonTime')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="time"
              value={commonStart}
              onChange={(e) => handleCommonTimeChange('start', e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded bg-bgElevated text-textDefault focus:ring-primary focus:border-primary"
            />
            <span className="text-textTertiary text-sm">-</span>
            <input
              type="time"
              value={commonEnd}
              onChange={(e) => handleCommonTimeChange('end', e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded bg-bgElevated text-textDefault focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-medium text-textTertiary mb-1">{t('drivers.schedule.weekdays')}</p>
        <div className="grid grid-cols-2 gap-1">
          {WEEKDAY_INDICES.map((index) => {
            const dayKey = DAY_KEYS[index];
            const day = schedule[index];
            return (
              <DayItem
                key={dayKey}
                label={t(`drivers.schedule.days.${dayKey}`)}
                day={day}
                index={index}
                scheduleMode={scheduleMode}
                sameForAll={sameForAll}
                commonStart={commonStart}
                commonEnd={commonEnd}
                readOnly={readOnly}
                onToggle={toggleDay}
                onUpdateTime={updateTime}
                noWorkText={t('drivers.schedule.noWorkTime')}
                alwaysBusinessText={t('drivers.schedule.mode.alwaysBusiness')}
              />
            );
          })}
        </div>

        <p className="text-xs font-medium text-textTertiary mt-3 mb-1">{t('drivers.schedule.weekend')}</p>
        <div className="grid grid-cols-2 gap-1">
          {WEEKEND_INDICES.map((index) => {
            const dayKey = DAY_KEYS[index];
            const day = schedule[index];
            return (
              <DayItem
                key={dayKey}
                label={t(`drivers.schedule.days.${dayKey}`)}
                day={day}
                index={index}
                scheduleMode={scheduleMode}
                sameForAll={sameForAll}
                commonStart={commonStart}
                commonEnd={commonEnd}
                readOnly={readOnly}
                onToggle={toggleDay}
                onUpdateTime={updateTime}
                noWorkText={t('drivers.schedule.noWorkTime')}
                alwaysBusinessText={t('drivers.schedule.mode.alwaysBusiness')}
              />
            );
          })}
        </div>
      </div>

      <p className="text-xs text-textTertiary">
        {scheduleMode === 'private_manual'
          ? t('drivers.schedule.noSchedule')
          : scheduleMode === 'company_car'
            ? t('drivers.schedule.mode.companyCarHelp')
            : enabledCount === 0
              ? t('drivers.schedule.noSchedule')
              : `${weekdayCount} ${t('drivers.schedule.weekdaysActive')}${weekendCount > 0 ? `, ${weekendCount} ${t('drivers.schedule.weekendActive')}` : ''}`}
      </p>

      {!readOnly && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {saving ? (
            t('common.saving')
          ) : (
            <>
              <CheckIcon className="w-4 h-4" />
              {draftMode ? t('drivers.schedule.saveDraft') : t('common.save')}
            </>
          )}
        </button>
      )}
    </div>
  );
};

const DayItem: React.FC<{
  label: string;
  day: DaySchedule;
  index: number;
  scheduleMode: DriverScheduleMode;
  sameForAll: boolean;
  commonStart: string;
  commonEnd: string;
  readOnly: boolean;
  onToggle: (index: number) => void;
  onUpdateTime: (index: number, field: 'start_time' | 'end_time', value: string) => void;
  noWorkText: string;
  alwaysBusinessText: string;
}> = ({
  label,
  day,
  index,
  scheduleMode,
  sameForAll,
  commonStart,
  commonEnd,
  readOnly,
  onToggle,
  onUpdateTime,
  noWorkText,
  alwaysBusinessText,
}) => {
  const isWorkHours = scheduleMode === 'work_hours';
  const isCompanyCar = scheduleMode === 'company_car';

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded transition-colors ${
        day.enabled || isCompanyCar ? 'bg-BgPrimaryLight/30' : 'bg-bgLight/30'
      }`}
    >
      {isWorkHours && !readOnly && (
        <input
          type="checkbox"
          checked={day.enabled}
          onChange={() => onToggle(index)}
          className="rounded border-border text-primary focus:ring-primary flex-shrink-0"
        />
      )}
      <span
        className={`text-sm w-8 flex-shrink-0 ${
          day.enabled || isCompanyCar ? 'text-textDefault font-medium' : 'text-textTertiary'
        }`}
      >
        {label}
      </span>
      {isCompanyCar ? (
        <span className="text-xs text-textSecondary">{alwaysBusinessText}</span>
      ) : day.enabled && isWorkHours ? (
        sameForAll ? (
          <span className="text-xs text-textSecondary">
            {commonStart}-{commonEnd}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <input
              type="time"
              value={day.start_time}
              onChange={(e) => onUpdateTime(index, 'start_time', e.target.value)}
              disabled={readOnly}
              className="px-1 py-0.5 text-xs border border-border rounded bg-bgElevated text-textDefault w-20"
            />
            <span className="text-textTertiary text-xs">-</span>
            <input
              type="time"
              value={day.end_time}
              onChange={(e) => onUpdateTime(index, 'end_time', e.target.value)}
              disabled={readOnly}
              className="px-1 py-0.5 text-xs border border-border rounded bg-bgElevated text-textDefault w-20"
            />
          </div>
        )
      ) : (
        <span className="text-xs text-textTertiary italic truncate">{noWorkText}</span>
      )}
    </div>
  );
};

export default DriverScheduleEditor;
